// src/Components/Modules/HR/RecruitmentPipeline.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  Divider,
  Badge as MuiBadge,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  FaPlus,
  FaCheck,
  FaTimes,
  FaBriefcase,
  FaUsers,
  FaUserTie,
  FaEnvelope,
  FaPhone,
  FaCalendarAlt,
  FaSearch,
  FaFilter,
  FaSync,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEye,
  FaDownload,
  FaUserPlus,
  FaBuilding,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaStar,
  FaRegStar,
  FaUser,
  FaInfoCircle,
  FaArrowRight,
  FaClipboardList,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
}));

const JobCard = styled(Card)(({ theme, selected }) => ({
  borderRadius: theme.spacing(2),
  border: selected ? "2px solid #667eea" : "1px solid rgba(0,0,0,0.08)",
  background: selected ? "linear-gradient(135deg, #f5f7ff 0%, #e8ecff 100%)" : "white",
  transition: "all 0.3s ease",
  cursor: "pointer",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 20px rgba(102, 126, 234, 0.15)",
    borderColor: "#667eea",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(0.6, 1.5),
  minWidth: "auto",
  ...(variant === "hire" && {
    background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    color: "#1b5e20",
    "&:hover": {
      background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
      boxShadow: "0 4px 12px rgba(67, 233, 123, 0.3)",
    },
  }),
  ...(variant === "reject" && {
    background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    color: "#b71c1c",
    "&:hover": {
      background: "linear-gradient(135deg, #fee140 0%, #fa709a 100%)",
      boxShadow: "0 4px 12px rgba(250, 112, 154, 0.3)",
    },
  }),
}));

const RecruitmentPipeline = () => {
  const [jobs, setJobs] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [searchJobs, setSearchJobs] = useState("");
  const [searchCandidates, setSearchCandidates] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // ✅ Job Creation States
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [jobForm, setJobForm] = useState({
    title: '',
    department: '',
    description: '',
    requirements: '',
    salary_min: '',
    salary_max: '',
    location: '',
    deadline: '',
  });

  // ✅ Candidate Creation States
  const [candidateDialogOpen, setCandidateDialogOpen] = useState(false);
  const [candidateForm, setCandidateForm] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    expected_salary: '',
    cv_link: '',
    status: 'pending'
  });

  const role = Number(localStorage.getItem("who_am_i"));
  const canManage = role <= 2;

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoadingJobs(true);
    setError(null);
    try {
      const res = await AxiosAuthInstance.get("hr/recruitment/jobs");
      const jobsData = res.data.job || res.data;
      setJobs(Array.isArray(jobsData) ? jobsData : []);
    } catch (err) {
      setError("Failed to load jobs");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load job listings. Please try again.",
      });
    } finally {
      setLoadingJobs(false);
    }
  };

  const loadCandidates = async (jobId) => {
    setActiveJob(jobId);
    setLoadingCandidates(true);
    try {
      const res = await AxiosAuthInstance.get(`hr/recruitment/candidates/${jobId}`);
      const candidatesData = res.data.candidate || res.data;
      setCandidates(Array.isArray(candidatesData) ? candidatesData : []);
    } catch (err) {
      setCandidates([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load candidates. Please try again.",
      });
    } finally {
      setLoadingCandidates(false);
    }
  };

  const updateStatus = async (id, status) => {
    const statusText = status === "hired" ? "hire" : "reject";
    const result = await Swal.fire({
      title: `${status === "hired" ? "Hire" : "Reject"} Candidate?`,
      html: `
        <div style="text-align: left; padding: 10px;">
          <p>Are you sure you want to <strong>${statusText}</strong> this candidate?</p>
          <p style="color: #757575; font-size: 14px;">This action will update their application status.</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: status === "hired" ? "#4caf50" : "#f44336",
      cancelButtonColor: "#757575",
      confirmButtonText: `Yes, ${status === "hired" ? "Hire" : "Reject"}`,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setActionLoading(id);
      try {
        await AxiosAuthInstance.post(`hr/recruitment/candidates/${id}/status`, { status });
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Candidate ${statusText}d successfully`,
          timer: 2000,
          showConfirmButton: false,
        });
        loadCandidates(activeJob);
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to update status";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setActionLoading(null);
      }
    }
  };

  // ✅ Create Job Function
  const createJob = async () => {
    if (!jobForm.title || !jobForm.department) {
      Swal.fire({ 
        icon: "error", 
        title: "Error", 
        text: "Title and Department are required" 
      });
      return;
    }
    
    setActionLoading('create_job');
    try {
      await AxiosAuthInstance.post("hr/recruitment/jobs", jobForm);
      Swal.fire({ 
        icon: "success", 
        title: "Job Posted!", 
        text: "New job has been created successfully",
        timer: 2000, 
        showConfirmButton: false 
      });
      setJobDialogOpen(false);
      setJobForm({
        title: '',
        department: '',
        description: '',
        requirements: '',
        salary_min: '',
        salary_max: '',
        location: '',
        deadline: '',
      });
      loadJobs();
    } catch (err) {
      Swal.fire({ 
        icon: "error", 
        title: "Error", 
        text: err.response?.data?.message || "Failed to create job" 
      });
    } finally {
      setActionLoading(null);
    }
  };

  // ✅ Add Candidate Function
  const addCandidate = async () => {
    if (!candidateForm.name || !candidateForm.phone) {
      Swal.fire({ icon: "error", title: "Error", text: "Name and Phone are required" });
      return;
    }
    setActionLoading('add_candidate');
    try {
      await AxiosAuthInstance.post("hr/recruitment/candidates", { ...candidateForm, job_id: activeJob });
      Swal.fire({ icon: "success", title: "Candidate Added!", timer: 1500, showConfirmButton: false });
      setCandidateDialogOpen(false);
      setCandidateForm({ name: '', email: '', phone: '', position: '', expected_salary: '', cv_link: '', status: 'pending' });
      loadCandidates(activeJob);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Failed to add candidate" });
    } finally {
      setActionLoading(null);
    }
  };

  const filteredJobs = useMemo(() => {
    if (!Array.isArray(jobs)) return [];
    return jobs.filter((job) => {
      const title = job.title?.toLowerCase() || "";
      const department = job.department?.toLowerCase() || "";
      const searchTerm = searchJobs.toLowerCase();
      return title.includes(searchTerm) || department.includes(searchTerm);
    });
  }, [jobs, searchJobs]);

  const filteredCandidates = useMemo(() => {
    if (!Array.isArray(candidates)) return [];
    return candidates.filter((candidate) => {
      const name = candidate.name?.toLowerCase() || "";
      const email = candidate.email?.toLowerCase() || "";
      const status = candidate.status?.toLowerCase() || "";
      const searchTerm = searchCandidates.toLowerCase();

      const matchesSearch = name.includes(searchTerm) || email.includes(searchTerm);
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [candidates, searchCandidates, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCandidates = filteredCandidates.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const stats = useMemo(() => {
    const totalJobs = jobs.length;
    const totalCandidates = candidates.length;
    const hired = candidates.filter((c) => c.status === "hired").length;
    const pending = candidates.filter((c) => c.status === "pending" || !c.status).length;
    return { totalJobs, totalCandidates, hired, pending };
  }, [jobs, candidates]);

  const activeJobData = useMemo(() => {
    return jobs.find((j) => j.id === activeJob);
  }, [jobs, activeJob]);

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "hired") {
      return (
        <Chip
          icon={<FaCheckCircle style={{ fontSize: 12 }} />}
          label="Hired"
          size="small"
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#4caf50" },
          }}
        />
      );
    }
    if (statusLower === "rejected") {
      return (
        <Chip
          icon={<FaTimesCircle style={{ fontSize: 12 }} />}
          label="Rejected"
          size="small"
          sx={{
            bgcolor: "#ffebee",
            color: "#c62828",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#f44336" },
          }}
        />
      );
    }
    if (statusLower === "interview") {
      return (
        <Chip
          icon={<FaUserTie style={{ fontSize: 12 }} />}
          label="Interview"
          size="small"
          sx={{
            bgcolor: "#e3f2fd",
            color: "#1565c0",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#2196f3" },
          }}
        />
      );
    }
    return (
      <Chip
        icon={<FaClock style={{ fontSize: 12 }} />}
        label="Pending"
        size="small"
        sx={{
          bgcolor: "#fff3e0",
          color: "#ef6c00",
          fontWeight: 600,
          "& .MuiChip-icon": { color: "#ff9800" },
        }}
      />
    );
  };

  const getInitials = (name) => {
    if (!name) return "N/A";
    const names = name.split(" ");
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getColorFromName = (name) => {
    const colors = [
      "#667eea", "#764ba2", "#f093fb", "#4facfe",
      "#43e97b", "#fa709a", "#fee140", "#30cfd0",
    ];
    if (!name) return colors[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loadingJobs && jobs.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Recruitment / ATS" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Recruitment / ATS" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaUserTie style={{ marginRight: 10, verticalAlign: "middle" }} />
              Recruitment Pipeline
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Manage job openings and track candidates
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            {canManage && (
              <Button
                variant="contained"
                startIcon={<FaPlus />}
                onClick={() => setJobDialogOpen(true)}
                sx={{
                  background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                  color: "white",
                  fontWeight: 600,
                  "&:hover": {
                    background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
                  }
                }}
              >
                Post New Job
              </Button>
            )}
            <Tooltip title="Refresh Data">
              <IconButton
                onClick={loadJobs}
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "white",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
                }}
              >
                <FaSync />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: "#e3f2fd", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaBriefcase size={28} color="#2196f3" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#1565c0" sx={{ mb: 0.5 }}>
              {stats.totalJobs}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Active Jobs
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: "#f3e5f5", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaUsers size={28} color="#7b1fa2" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#7b1fa2" sx={{ mb: 0.5 }}>
              {stats.totalCandidates}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Total Candidates
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: "#e8f5e9", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaCheckCircle size={28} color="#4caf50" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#2e7d32" sx={{ mb: 0.5 }}>
              {stats.hired}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Hired
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box sx={{ width: 60, height: 60, borderRadius: "50%", bgcolor: "#fff3e0", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
              <FaClock size={28} color="#ff9800" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#ef6c00" sx={{ mb: 0.5 }}>
              {stats.pending}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Pending Review
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Jobs List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              <FaBriefcase style={{ marginRight: 8, verticalAlign: "middle" }} />
              Job Openings
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <TextField
              placeholder="Search jobs..."
              value={searchJobs}
              onChange={(e) => setSearchJobs(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaSearch style={{ color: "#757575" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <Stack spacing={2}>
              {loadingJobs ? (
                Array.from(new Array(3)).map((_, index) => (
                  <Skeleton key={index} variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
                ))
              ) : filteredJobs.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <FaBriefcase style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                  <Typography variant="body2" color="text.secondary">
                    No jobs found
                  </Typography>
                </Box>
              ) : (
                filteredJobs.map((job) => (
                  <JobCard
                    key={job.id}
                    selected={activeJob === job.id}
                    elevation={0}
                    onClick={() => loadCandidates(job.id)}
                  >
                    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <Avatar sx={{ bgcolor: "#667eea", width: 40, height: 40 }}>
                          <FaBriefcase />
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                            {job.title}
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                            <FaBuilding style={{ fontSize: 12, color: "#757575" }} />
                            <Typography variant="caption" color="text.secondary">
                              {job.department || "N/A"}
                            </Typography>
                          </Box>
                          {job.location && (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FaMapMarkerAlt style={{ fontSize: 12, color: "#757575" }} />
                              <Typography variant="caption" color="text.secondary">
                                {job.location}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <FaArrowRight style={{ color: "#667eea", marginTop: 8 }} />
                      </Box>
                    </CardContent>
                  </JobCard>
                ))
              )}
            </Stack>
          </Paper>
        </Grid>

        {/* Candidates List */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                <FaUsers style={{ marginRight: 8, verticalAlign: "middle" }} />
                {activeJobData ? (
                  <>Candidates for {activeJobData.title}</>
                ) : (
                  "Select a Job"
                )}
              </Typography>
              {activeJob && (
                <Stack direction="row" spacing={1}>
                  <Chip
                    label={`${filteredCandidates.length} candidates`}
                    size="small"
                    sx={{ bgcolor: "#e3f2fd", color: "#1976d2", fontWeight: 600 }}
                  />
                  {/* ✅ Add Candidate Button */}
                  {canManage && (
                    <Button 
                      variant="contained" 
                      size="small" 
                      startIcon={<FaUserPlus />} 
                      onClick={() => setCandidateDialogOpen(true)}
                      sx={{ 
                        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", 
                        color: "white",
                        fontWeight: 600
                      }}
                    >
                      Add Candidate
                    </Button>
                  )}
                </Stack>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {!activeJob ? (
              <Box sx={{ textAlign: "center", py: 8 }}>
                <FaUserTie style={{ fontSize: 64, color: "#bdbdbd", marginBottom: 16 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Select a Job Opening
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click on a job from the left panel to view candidates
                </Typography>
              </Box>
            ) : (
              <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    placeholder="Search candidates..."
                    value={searchCandidates}
                    onChange={(e) => setSearchCandidates(e.target.value)}
                    variant="outlined"
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaSearch style={{ color: "#757575" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                  <TextField
                    select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    variant="outlined"
                    size="small"
                    sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="interview">Interview</MenuItem>
                    <MenuItem value="hired">Hired</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </TextField>
                </Stack>

                <StyledTableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#fafafa" }}>
                        <StyledTableCell width={80}>SL</StyledTableCell>
                        <StyledTableCell>Candidate</StyledTableCell>
                        <StyledTableCell>Contact</StyledTableCell>
                        <StyledTableCell>Exp. Salary</StyledTableCell>
                        <StyledTableCell>Status</StyledTableCell>
                        {canManage && <StyledTableCell align="center">Actions</StyledTableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingCandidates ? (
                        Array.from(new Array(5)).map((_, index) => (
                          <TableRow key={index}>
                            <TableCell><Skeleton width={40} /></TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                <Skeleton variant="circular" width={40} height={40} />
                                <Skeleton width={120} />
                              </Box>
                            </TableCell>
                            <TableCell><Skeleton width={150} /></TableCell>
                            <TableCell><Skeleton width={80} /></TableCell>
                            <TableCell><Skeleton width={80} /></TableCell>
                            {canManage && <TableCell><Skeleton width={120} /></TableCell>}
                          </TableRow>
                        ))
                      ) : paginatedCandidates.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={canManage ? 6 : 5} align="center" sx={{ py: 8 }}>
                            <Box sx={{ textAlign: "center" }}>
                              <FaUser style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                              <Typography variant="h6" color="text.secondary" gutterBottom>
                                No candidates found
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {searchCandidates || statusFilter !== "all"
                                  ? "Try adjusting your filters"
                                  : "No candidates have applied for this position yet"}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedCandidates.map((row, index) => {
                          const statusLower = row.status?.toLowerCase();
                          const isPending = statusLower === "pending" || !statusLower;

                          return (
                            <StyledTableRow key={row.id || index} hover>
                              <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                  <Avatar
                                    sx={{
                                      bgcolor: getColorFromName(row.name),
                                      width: 40,
                                      height: 40,
                                      fontWeight: 600,
                                    }}
                                  >
                                    {getInitials(row.name)}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={600}>
                                      {row.name || "N/A"}
                                    </Typography>
                                    {row.position && (
                                      <Typography variant="caption" color="text.secondary">
                                        {row.position}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                  {row.email && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <FaEnvelope style={{ fontSize: 12, color: "#757575" }} />
                                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                        {row.email}
                                      </Typography>
                                    </Box>
                                  )}
                                  {row.phone && (
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                      <FaPhone style={{ fontSize: 12, color: "#757575" }} />
                                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                        {row.phone}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>{row.expected_salary ? `৳${Number(row.expected_salary).toLocaleString()}` : '-'}</TableCell>
                              <TableCell>{getStatusChip(row.status)}</TableCell>
                              {canManage && (
                                <TableCell align="center">
                                  {isPending || statusLower === "interview" ? (
                                    <Stack direction="row" spacing={0.5} justifyContent="center">
                                      <Tooltip title="Hire">
                                        <span>
                                          <ActionButton
                                            variant="hire"
                                            size="small"
                                            disabled={actionLoading === row.id}
                                            onClick={() => updateStatus(row.id, "hired")}
                                            startIcon={actionLoading === row.id ? null : <FaCheck />}
                                          >
                                            {actionLoading === row.id ? "..." : "Hire"}
                                          </ActionButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Reject">
                                        <span>
                                          <ActionButton
                                            variant="reject"
                                            size="small"
                                            disabled={actionLoading === row.id}
                                            onClick={() => updateStatus(row.id, "rejected")}
                                            startIcon={actionLoading === row.id ? null : <FaTimes />}
                                          >
                                            {actionLoading === row.id ? "..." : "Reject"}
                                          </ActionButton>
                                        </span>
                                      </Tooltip>
                                    </Stack>
                                  ) : (
                                    <Chip
                                      label="Processed"
                                      size="small"
                                      sx={{ bgcolor: "#f5f5f5", color: "#757575", fontWeight: 600 }}
                                    />
                                  )}
                                </TableCell>
                              )}
                            </StyledTableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>

                  {!loadingCandidates && filteredCandidates.length > 0 && (
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      component="div"
                      count={filteredCandidates.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{
                        borderTop: 1,
                        borderColor: "divider",
                        "& .MuiTablePagination-toolbar": {
                          paddingRight: 2,
                        },
                      }}
                    />
                  )}
                </StyledTableContainer>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* ✅ Create Job Dialog */}
      <Dialog open={jobDialogOpen} onClose={() => setJobDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#f5f7fa", fontWeight: 700 }}>
          Post New Job
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <TextField
              label="Job Title *"
              value={jobForm.title}
              onChange={(e) => setJobForm({...jobForm, title: e.target.value})}
              fullWidth
              required
            />
            <TextField
              label="Department *"
              value={jobForm.department}
              onChange={(e) => setJobForm({...jobForm, department: e.target.value})}
              fullWidth
              required
            />
            <TextField
              label="Job Description"
              value={jobForm.description}
              onChange={(e) => setJobForm({...jobForm, description: e.target.value})}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Requirements"
              value={jobForm.requirements}
              onChange={(e) => setJobForm({...jobForm, requirements: e.target.value})}
              fullWidth
              multiline
              rows={3}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Salary Min (৳)"
                type="number"
                value={jobForm.salary_min}
                onChange={(e) => setJobForm({...jobForm, salary_min: e.target.value})}
                fullWidth
              />
              <TextField
                label="Salary Max (৳)"
                type="number"
                value={jobForm.salary_max}
                onChange={(e) => setJobForm({...jobForm, salary_max: e.target.value})}
                fullWidth
              />
            </Box>
            <TextField
              label="Location"
              value={jobForm.location}
              onChange={(e) => setJobForm({...jobForm, location: e.target.value})}
              fullWidth
            />
            <TextField
              label="Application Deadline"
              type="date"
              value={jobForm.deadline}
              onChange={(e) => setJobForm({...jobForm, deadline: e.target.value})}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#f5f7fa" }}>
          <Button onClick={() => setJobDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={createJob}
            disabled={actionLoading === 'create_job'}
            sx={{ 
              background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
              }
            }}
          >
            {actionLoading === 'create_job' ? 'Posting...' : 'Post Job'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Add Candidate Dialog */}
      <Dialog open={candidateDialogOpen} onClose={() => setCandidateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#f5f7fa", fontWeight: 700 }}>
          Add New Candidate
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <Typography variant="subtitle2" color="primary" fontWeight={600}>
              Applying for: {activeJobData?.title}
            </Typography>
            <TextField
              label="Candidate Name *"
              value={candidateForm.name}
              onChange={(e) => setCandidateForm({...candidateForm, name: e.target.value})}
              fullWidth
              required
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Phone *"
                value={candidateForm.phone}
                onChange={(e) => setCandidateForm({...candidateForm, phone: e.target.value})}
                fullWidth
                required
              />
              <TextField
                label="Email"
                value={candidateForm.email}
                onChange={(e) => setCandidateForm({...candidateForm, email: e.target.value})}
                fullWidth
              />
            </Box>
            <TextField
              label="Current/Expected Position"
              value={candidateForm.position}
              onChange={(e) => setCandidateForm({...candidateForm, position: e.target.value})}
              fullWidth
            />
            <TextField
              label="Expected Salary (৳)"
              type="number"
              value={candidateForm.expected_salary}
              onChange={(e) => setCandidateForm({...candidateForm, expected_salary: e.target.value})}
              fullWidth
            />
            <TextField
              label="CV / Portfolio Link"
              value={candidateForm.cv_link}
              onChange={(e) => setCandidateForm({...candidateForm, cv_link: e.target.value})}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FaClipboardList style={{ color: "#757575" }} />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Current Status</InputLabel>
              <Select 
                value={candidateForm.status} 
                label="Current Status" 
                onChange={(e) => setCandidateForm({...candidateForm, status: e.target.value})}
              >
                <MenuItem value="pending">Pending Review</MenuItem>
                <MenuItem value="interview">Interview Scheduled</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: "#f5f7fa" }}>
          <Button onClick={() => setCandidateDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={addCandidate}
            disabled={actionLoading === 'add_candidate'}
            sx={{ 
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              }
            }}
          >
            {actionLoading === 'add_candidate' ? 'Adding...' : 'Add Candidate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default RecruitmentPipeline;